import { useEffect } from "react"
import { Calendar, dateFnsLocalizer, Views } from "react-big-calendar"
import { parseISO, formatISO, isBefore, isAfter, startOfDay } from "date-fns"
import "react-big-calendar/lib/css/react-big-calendar.css"
import "./calendar-styles.css"
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop"
import "react-big-calendar/lib/addons/dragAndDrop/styles.css"
import EventComponent from "./event-component"
import CustomToolbar from "./custom-toolbar"
import DayHeader from "./day-header"
import useGetCalendar from "@/modules/calendar/queries/use-get-calendar"
import type { UserEvent } from "@/services/api/cronos/user/events/get-all"
import { TriangleAlert } from "lucide-react"
import { isClientError } from "@educabot/fetcher"
import isLocked from "@/modules/calendar/utils/is-locked"
import useCalendarConfig from "@/modules/calendar/stores/use-calendar-config"
import useEvents from "@/modules/calendar/stores/use-events"
import getCalendarFormats from "@/modules/calendar/utils/get-calendar-formats"
import useCalendarEvents from "@/modules/calendar/hooks/use-calendar-events"
import { useDebounce } from "@uidotdev/usehooks"
import config from "@/modules/calendar/config/calendar-config"
import { useSidebar } from "@/components/ui/sidebar"
import { useProductTour } from "@/modules/product-tour/stores/use-product-tour"

function combineToISOUTC(dateStr: string, timeStr: string): string {
  const combinedDateTimeStr = `${dateStr}T${timeStr}:00`
  const dateTime = parseISO(combinedDateTimeStr)
  return formatISO(dateTime)
}

const localizer = dateFnsLocalizer(config)
const DnDCalendar = withDragAndDrop(Calendar)

export default function WeekCalendar() {
  const { format, currentWeek, currentDate, step, setCurrentWeek, ranges } = useCalendarConfig()
  const { startDate, endDate } = ranges
  const { state } = useSidebar()
  const { isActive } = useProductTour()
  const formats = getCalendarFormats(format)

  const { isPending, isError, data, error } = useGetCalendar({
    startDate: currentWeek.startDate,
    endDate: currentWeek.endDate,
  })

  const { events, setEvents } = useEvents()
  const { onEventDrop, onEventResize, onNavigate } = useCalendarEvents()
  const debouncedCurrentDate = useDebounce(currentDate, 500)

  useEffect(() => {
    setCurrentWeek(startOfDay(parseISO(debouncedCurrentDate)).toISOString())
  }, [debouncedCurrentDate])

  useEffect(() => {
    const newEvents = data?.data.description

    if (newEvents) {
      setEvents(newEvents)
    }
  }, [data])

  useEffect(() => {
    if (isActive) {
      window.scrollTo({ top: 0, behavior: "smooth" })
      return
    }
    window.scrollTo({ top: 815, behavior: "smooth" })
  }, [isActive])

  const dayPropGetter = (date: Date) => {
    const currentDay = startOfDay(date)
    const startDay = startOfDay(parseISO(startDate))
    const endDay = startOfDay(parseISO(endDate))

    if (isBefore(currentDay, startDay) || isAfter(currentDay, endDay)) {
      return { className: "opacity-20 bg-gray-50" }
    }

    return {}
  }

  if (isError) {
    return (
      <div className="mt-60 flex h-full w-full select-none flex-col items-center justify-start gap-3">
        <TriangleAlert className="size-9 text-red-500" />
        <p className="text-sm text-gray-500">Algo salió mal</p>
        <p className="text-sm text-gray-500">
          {isClientError(error) ? error.message : error?.message}
        </p>
      </div>
    )
  }

  return (
    <div className="relative h-auto w-full" data-sidebar-state={state}>
      {isPending && (
        <div className="absolute inset-0 z-30 flex items-start justify-center bg-gray-50 bg-opacity-60 pt-56 after:absolute after:h-16 after:w-16 after:animate-spin after:rounded-full after:border-4 after:border-primary after:border-t-transparent after:content-['']"></div>
      )}

      <DnDCalendar
        localizer={localizer}
        events={events.map((event) => ({
          ...event,
          start: parseISO(event.startDate),
          end: parseISO(event.endDate),
        }))}
        defaultView={Views.WEEK}
        views={[Views.WEEK]}
        step={step}
        dayPropGetter={dayPropGetter}
        showMultiDayTimes
        defaultDate={parseISO(currentDate)}
        date={parseISO(currentDate)}
        formats={formats}
        min={parseISO(combineToISOUTC(ranges.startDate, ranges.startTime))}
        max={parseISO(combineToISOUTC(ranges.endDate, ranges.endTime))}
        draggableAccessor={(event) => !isLocked(event as UserEvent)}
        resizableAccessor={(event) => !isLocked(event as UserEvent)}
        components={{
          event: EventComponent,
          toolbar: (props) => (
            <CustomToolbar
              currentDate={props.date}
              goToCurrentWeek={() => props.onNavigate("TODAY")}
              handleNavigation={props.onNavigate}
            />
          ),
          header: DayHeader,
        }}
        onNavigate={onNavigate}
        onEventDrop={onEventDrop}
        onEventResize={onEventResize}
        resizable
      />
    </div>
  )
}
